import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Calendar, AlertTriangle, X, LayoutGrid, List as ListIcon, ChevronUp, ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useLeadPipelineConfig from '../hooks/useLeadPipelineConfig';
import LeadPipelineCard from '../components/leads/LeadPipelineCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Lead-level Pipeline Kanban (VSL funnel, Batch 9 finale) ─────────────────
// Mirrors the Deal Pipeline board (native HTML5 drag-and-drop), but each card is
// a LEAD (contact) and columns come from the org's configurable pipeline stages.
// Moving a card PUTs /contacts/{id}/pipeline-stage; Won/Lost drops open a modal
// to capture product/value or reason before the stage write.

const BAND_DOT = { green: 'bg-emerald-400', amber: 'bg-amber-500', red: 'bg-red-500' };
const BAND_TEXT = { green: 'text-stone-400', amber: 'text-amber-600', red: 'text-red-600' };
const BAND_BORDER = { green: 'border-l-transparent', amber: 'border-l-amber-400', red: 'border-l-red-500' };
const BIC_CLS = { action_needed: 'bg-amber-100 text-amber-700', waiting_on_lead: 'bg-slate-100 text-slate-600' };

const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return null; }
};
const fmtMoney = (v, ccy) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return `${v} ${ccy || ''}`.trim();
    const num = n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : `${n}`;
    return `${num} ${ccy || 'USD'}`.trim();
};

function Badge({ children, cls }) {
    return <span className={`inline-block text-[11px] font-medium px-1.5 py-0.5 rounded-full ${cls || 'bg-stone-100 text-stone-600'}`}>{children}</span>;
}

// Deterministic color for a coach initial badge (stable per name).
const COACH_COLORS = [
    'bg-violet-100 text-violet-700', 'bg-sky-100 text-sky-700', 'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700', 'bg-indigo-100 text-indigo-700',
    'bg-teal-100 text-teal-700', 'bg-fuchsia-100 text-fuchsia-700',
];
const coachColor = (name) => {
    if (!name) return 'bg-stone-100 text-stone-600';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return COACH_COLORS[h % COACH_COLORS.length];
};
function CoachBadge({ name }) {
    if (!name) return null;
    return (
        <span title={name}
            className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0 ${coachColor(name)}`}>
            {name.charAt(0).toUpperCase()}
        </span>
    );
}

// ─── Won modal ───────────────────────────────────────────────────────────────
function WonModal({ open, onClose, lead, products, onConfirm, busy }) {
    const [productId, setProductId] = useState('');
    const [productName, setProductName] = useState('');
    const [value, setValue] = useState('');
    const [currency, setCurrency] = useState('USD');

    useEffect(() => {
        if (open) {
            setProductId('');
            setProductName(lead?.product_sold_name || '');
            setValue(lead?.product_value ?? '');
            setCurrency(lead?.product_value_currency || 'USD');
        }
    }, [open, lead]);

    const onPickProduct = (id) => {
        setProductId(id);
        const p = products.find(x => (x.product_id || x.id) === id);
        if (p) {
            setProductName(p.name || '');
            if (p.amount != null) setValue(p.amount);
            if (p.currency) setCurrency(p.currency);
        }
    };

    const save = () => {
        const payload = { product_sold_name: productName || null };
        if (productId) payload.product_sold_id = productId;
        payload.product_value = value === '' ? null : Number(value);
        payload.product_value_currency = currency || 'USD';
        onConfirm(payload);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Mark {lead?.full_name || 'Lead'} as Won</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    {products.length > 0 ? (
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Product</label>
                            <select value={productId} onChange={e => onPickProduct(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="">Select a product…</option>
                                {products.map(p => <option key={p.product_id || p.id} value={p.product_id || p.id}>{p.name}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Product Name</label>
                            <input value={productName} onChange={e => setProductName(e.target.value)} placeholder="e.g. Annual Coaching Program"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Value</label>
                            <input type="number" value={value} onChange={e => setValue(e.target.value)} placeholder="5000"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Currency</label>
                            <input value={currency} onChange={e => setCurrency(e.target.value)} placeholder="USD"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700 disabled:opacity-50">Cancel</button>
                    <button onClick={() => onConfirm(null)} disabled={busy} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700 disabled:opacity-50">Move without details</button>
                    <button onClick={save} disabled={busy} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                        {busy ? 'Saving…' : 'Save & mark Won'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Lost modal ──────────────────────────────────────────────────────────────
function LostModal({ open, onClose, lead, reasonOptions, onConfirm, busy }) {
    const [reason, setReason] = useState('');
    const [other, setOther] = useState('');

    useEffect(() => {
        if (open) { setReason(lead?.reason_lost || ''); setOther(lead?.reason_lost_other || ''); }
    }, [open, lead]);

    const save = () => {
        const payload = { reason_lost: reason || null };
        if (reason === 'Other') payload.reason_lost_other = other || null;
        onConfirm(payload);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Mark {lead?.full_name || 'Lead'} as Lost</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Reason Lost</label>
                        <select value={reason} onChange={e => setReason(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                            <option value="">Select a reason…</option>
                            {reasonOptions.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    {reason === 'Other' && (
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Reason (Other)</label>
                            <input value={other} onChange={e => setOther(e.target.value)} placeholder="Describe…"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700 disabled:opacity-50">Cancel</button>
                    <button onClick={() => onConfirm(null)} disabled={busy} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700 disabled:opacity-50">Move without details</button>
                    <button onClick={save} disabled={busy} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                        {busy ? 'Saving…' : 'Save & mark Lost'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Lead card ───────────────────────────────────────────────────────────────
function LeadCard({ lead, isWonColumn, onDragStart, onDragEnd, onOpen }) {
    const band = lead.time_in_stage_band || 'green';
    const bic = lead.ball_in_court;
    const outcome = lead.sales_call_outcome;
    const bookingDate = fmtDate(lead.next_booking_at);
    const money = fmtMoney(lead.product_value, lead.product_value_currency);

    return (
        <div
            className={`bg-white rounded-lg border border-stone-200 border-l-4 ${BAND_BORDER[band]} p-3 cursor-pointer hover:border-stone-400 hover:shadow-md transition-all`}
            draggable
            onDragStart={e => onDragStart(e, lead)}
            onDragEnd={onDragEnd}
            onClick={() => onOpen(lead)}
            data-testid={`lead-card-${lead.contact_id}`}
        >
            {lead.company_name && (
                <p className="text-[11px] text-stone-400 font-medium truncate leading-tight">{lead.company_name}</p>
            )}
            <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-stone-900 leading-snug">{lead.full_name}</p>
                {lead.time_in_stage_days != null && (
                    <span className={`flex items-center gap-1 text-[11px] font-medium flex-shrink-0 ${BAND_TEXT[band]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${BAND_DOT[band]}`} />
                        {lead.time_in_stage_days}d
                    </span>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {bic && bic !== 'na' && (
                    <Badge cls={BIC_CLS[bic]}>{lead.ball_in_court_label || bic}</Badge>
                )}
                {lead.needs_followup && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3" /> Follow up
                    </span>
                )}
                {outcome && (
                    <Badge cls={outcome === 'Call Completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{outcome}</Badge>
                )}
            </div>

            <div className="flex items-center gap-3 mt-2">
                {lead.coach_assigned_name && (
                    <span className="flex items-center gap-1 min-w-0">
                        <CoachBadge name={lead.coach_assigned_name} />
                    </span>
                )}
                {bookingDate && (
                    <span className="text-xs text-stone-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />{bookingDate}
                    </span>
                )}
            </div>
            {isWonColumn && (money || lead.product_sold_name) && (
                <div className="mt-2 pt-2 border-t border-stone-100">
                    {money && <p className="text-sm font-semibold text-emerald-700">{money}</p>}
                    {lead.product_sold_name && <p className="text-xs text-stone-500 truncate">{lead.product_sold_name}</p>}
                </div>
            )}
        </div>
    );
}

// ─── Lead working drawer (G7) — right-side slide-in; embeds LeadPipelineCard ──
function LeadDrawer({ lead, stages, teamMembers, getAuthHeader, onClose, onStageChange }) {
    const navigate = useNavigate();
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stageSaving, setStageSaving] = useState(false);

    const contactId = lead?.contact_id;

    const fetchContact = useCallback(async () => {
        if (!contactId) return;
        try {
            const res = await axios.get(`${API}/contacts/${contactId}`, { headers: getAuthHeader() });
            setContact(res.data);
        } catch {
            toast.error('Failed to load lead');
        } finally {
            setLoading(false);
        }
    }, [contactId, getAuthHeader]);

    useEffect(() => { setLoading(true); fetchContact(); }, [fetchContact]);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const currentStageKey = contact?.pipeline_stage || lead?.pipeline_stage || '';

    const changeStage = async (stageKey) => {
        if (!stageKey || stageKey === currentStageKey) return;
        setStageSaving(true);
        try {
            await axios.put(`${API}/contacts/${contactId}/pipeline-stage`, { stage: stageKey }, { headers: getAuthHeader() });
            const label = stages.find(s => s.key === stageKey)?.label || stageKey;
            toast.success(`Moved to ${label}`);
            await fetchContact();
            if (onStageChange) onStageChange();
        } catch {
            toast.error('Failed to change stage');
        } finally {
            setStageSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50" data-testid="lead-drawer">
            {/* Dim backdrop — click to close */}
            <div className="absolute inset-0 bg-stone-900/30" onClick={onClose} />
            {/* Panel */}
            <div className="absolute top-0 right-0 h-full w-full max-w-[480px] bg-stone-50 shadow-2xl flex flex-col">
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-stone-200 bg-white">
                    <div className="min-w-0">
                        {lead?.company_name && <p className="text-xs text-stone-400 font-medium truncate">{lead.company_name}</p>}
                        <h2 className="text-lg font-bold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {lead?.full_name || contact?.first_name || 'Lead'}
                        </h2>
                        <div className="mt-2 flex items-center gap-2">
                            <label className="text-xs text-stone-400">Stage</label>
                            <select value={currentStageKey} disabled={stageSaving || loading}
                                onChange={e => changeStage(e.target.value)}
                                className="text-sm text-stone-800 bg-white border border-stone-200 hover:border-stone-300 focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 rounded-md px-2 py-1">
                                {!currentStageKey && <option value="">(unset)</option>}
                                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                            {stageSaving && <Loader2 className="w-3.5 h-3.5 text-stone-400 animate-spin" />}
                        </div>
                        <button onClick={() => navigate(`/contacts/${contactId}`)}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-stone-800"
                            data-nav-to="ContactDetail">
                            Open full page <ExternalLink className="w-3 h-3" />
                        </button>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:text-stone-800 hover:bg-stone-100 flex-shrink-0" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                    ) : contact ? (
                        <LeadPipelineCard
                            contact={contact}
                            contactId={contactId}
                            teamMembers={teamMembers}
                            getAuthHeader={getAuthHeader}
                            onChange={fetchContact}
                        />
                    ) : (
                        <p className="text-sm text-stone-400 text-center py-16">Lead not found.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function LeadPipeline() {
    const { getAuthHeader } = useAuth();
    const { config } = useLeadPipelineConfig();
    const [stages, setStages] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [followupOnly, setFollowupOnly] = useState(false);
    const [products, setProducts] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);

    // View mode (G8) + filters (G9)
    const [view, setView] = useState('board'); // 'board' | 'list'
    const [fCoach, setFCoach] = useState('');
    const [fStage, setFStage] = useState('');
    const [fBic, setFBic] = useState('');
    const [fBand, setFBand] = useState('');
    const [fSource, setFSource] = useState('');
    const [sortBy, setSortBy] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    // Working drawer (G7)
    const [drawerLead, setDrawerLead] = useState(null);

    // Drag + move state
    const [draggedLead, setDraggedLead] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Modals
    const [wonModal, setWonModal] = useState(null);   // { lead }
    const [lostModal, setLostModal] = useState(null); // { lead }

    const reasonOptions = config?.reason_lost_options || [];

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const loadBoard = useCallback(async (searchTerm) => {
        try {
            const params = {};
            if (searchTerm) params.search = searchTerm;
            const res = await axios.get(`${API}/lead-pipeline/board`, { headers: getAuthHeader(), params });
            const data = res.data || {};
            const sorted = (data.stages || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            setStages(sorted);
            setLeads(Array.isArray(data.leads) ? data.leads : []);
        } catch {
            toast.error('Failed to load lead pipeline');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { loadBoard(debouncedSearch); }, [debouncedSearch, loadBoard]);

    useEffect(() => {
        axios.get(`${API}/products/picker`, { headers: getAuthHeader() })
            .then(res => setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || [])))
            .catch(() => setProducts([]));
    }, [getAuthHeader]);

    // Org team members for the drawer's coach dropdown (one-time)
    useEffect(() => {
        axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() })
            .then(res => setTeamMembers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTeamMembers([]));
    }, [getAuthHeader]);

    // Distinct filter option lists, derived from loaded leads
    const coachOptions = useMemo(
        () => [...new Set(leads.map(l => l.coach_assigned_name).filter(Boolean))].sort(),
        [leads]);
    const sourceOptions = useMemo(
        () => [...new Set(leads.map(l => l.prospect_source).filter(Boolean))].sort(),
        [leads]);

    // ── Drag handlers (native HTML5) ──
    const handleDragStart = (e, lead) => {
        setDraggedLead(lead);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragEnd = () => { setDraggedLead(null); setDragOverStage(null); };
    const handleDragOver = (e, stageKey) => { e.preventDefault(); setDragOverStage(stageKey); };

    const moveStage = useCallback(async (lead, stageKey) => {
        const prevStage = lead.pipeline_stage;
        // optimistic
        setLeads(prev => prev.map(l => l.contact_id === lead.contact_id ? { ...l, pipeline_stage: stageKey } : l));
        try {
            await axios.put(`${API}/contacts/${lead.contact_id}/pipeline-stage`, { stage: stageKey }, { headers: getAuthHeader() });
            const label = stages.find(s => s.key === stageKey)?.label || stageKey;
            toast.success(`Moved to ${label}`);
            await loadBoard(debouncedSearch);
        } catch {
            setLeads(prev => prev.map(l => l.contact_id === lead.contact_id ? { ...l, pipeline_stage: prevStage } : l));
            toast.error('Failed to move lead');
        }
    }, [getAuthHeader, stages, loadBoard, debouncedSearch]);

    const handleDrop = (e, stageKey) => {
        e.preventDefault();
        setDragOverStage(null);
        const lead = draggedLead;
        setDraggedLead(null);
        if (!lead || lead.pipeline_stage === stageKey) return;
        if (stageKey === 'won') { setWonModal({ lead }); return; }
        if (stageKey === 'lost') { setLostModal({ lead }); return; }
        moveStage(lead, stageKey);
    };

    // ── Won/Lost confirm: optional detail PUT, then pipeline-stage PUT ──
    const confirmWon = async (detailPayload) => {
        if (submitting) return;
        const lead = wonModal.lead;
        setSubmitting(true);
        try {
            if (detailPayload) {
                await axios.put(`${API}/contacts/${lead.contact_id}`, detailPayload, { headers: getAuthHeader() });
            }
            await axios.put(`${API}/contacts/${lead.contact_id}/pipeline-stage`, { stage: 'won' }, { headers: getAuthHeader() });
            toast.success(`${lead.full_name} marked Won`);
            setWonModal(null);
            await loadBoard(debouncedSearch);
        } catch {
            toast.error('Failed to mark Won');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmLost = async (detailPayload) => {
        if (submitting) return;
        const lead = lostModal.lead;
        setSubmitting(true);
        try {
            if (detailPayload) {
                await axios.put(`${API}/contacts/${lead.contact_id}`, detailPayload, { headers: getAuthHeader() });
            }
            await axios.put(`${API}/contacts/${lead.contact_id}/pipeline-stage`, { stage: 'lost' }, { headers: getAuthHeader() });
            toast.success(`${lead.full_name} marked Lost`);
            setLostModal(null);
            await loadBoard(debouncedSearch);
        } catch {
            toast.error('Failed to mark Lost');
        } finally {
            setSubmitting(false);
        }
    };

    // Combined client-side filter chain (search is server-side; rest are local)
    const visibleLeads = useMemo(() => leads.filter(l => {
        if (followupOnly && !l.needs_followup) return false;
        if (fCoach && l.coach_assigned_name !== fCoach) return false;
        if (fStage && l.pipeline_stage !== fStage) return false;
        if (fBic && l.ball_in_court !== fBic) return false;
        if (fBand && (l.time_in_stage_band || 'green') !== fBand) return false;
        if (fSource && l.prospect_source !== fSource) return false;
        return true;
    }), [leads, followupOnly, fCoach, fStage, fBic, fBand, fSource]);

    const leadsByStage = (key) => visibleLeads.filter(l => l.pipeline_stage === key);
    const stageLabel = (key) => stages.find(s => s.key === key)?.label || key;
    const stageColor = (key) => stages.find(s => s.key === key)?.color || '#64748b';

    const anyFilterActive = followupOnly || fCoach || fStage || fBic || fBand || fSource;
    const clearFilters = () => { setFollowupOnly(false); setFCoach(''); setFStage(''); setFBic(''); setFBand(''); setFSource(''); };

    const openDrawer = (lead) => setDrawerLead(lead);
    const closeDrawer = useCallback(() => { setDrawerLead(null); loadBoard(debouncedSearch); }, [loadBoard, debouncedSearch]);

    // List view sorting (G8)
    const toggleSort = (key) => {
        if (sortBy === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortBy(key); setSortDir('asc'); }
    };
    const sortVal = (l, key) => {
        switch (key) {
            case 'full_name': return (l.full_name || '').toLowerCase();
            case 'company_name': return (l.company_name || '').toLowerCase();
            case 'pipeline_stage': return (l.pipeline_stage_label || stageLabel(l.pipeline_stage) || '').toLowerCase();
            case 'coach_assigned_name': return (l.coach_assigned_name || '').toLowerCase();
            case 'ball_in_court': return (l.ball_in_court_label || l.ball_in_court || '').toLowerCase();
            case 'time_in_stage_days': return l.time_in_stage_days ?? -1;
            case 'next_booking_at': return l.next_booking_at ? new Date(l.next_booking_at).getTime() : -1;
            case 'lead_status': return (l.lead_status || '').toLowerCase();
            default: return '';
        }
    };
    const sortedLeads = useMemo(() => {
        if (!sortBy) return visibleLeads;
        const arr = [...visibleLeads];
        arr.sort((a, b) => {
            const va = sortVal(a, sortBy), vb = sortVal(b, sortBy);
            if (va < vb) return sortDir === 'asc' ? -1 : 1;
            if (va > vb) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return arr;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleLeads, sortBy, sortDir, stages]);

    const SortHeader = ({ col, children, className = '' }) => (
        <th className={`px-3 py-2 text-left text-[11px] font-semibold text-stone-500 uppercase tracking-wider cursor-pointer select-none hover:text-stone-700 ${className}`}
            onClick={() => toggleSort(col)}>
            <span className="inline-flex items-center gap-1">
                {children}
                {sortBy === col && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
            </span>
        </th>
    );

    return (
        <div className="space-y-4">
            {/* Header / toolbar */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Lead Pipeline</h1>
                    <p className="text-sm text-stone-500 mt-0.5">{visibleLeads.length} lead{visibleLeads.length === 1 ? '' : 's'}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative min-w-48 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search leads…" value={search} onChange={e => setSearch(e.target.value)}
                            data-testid="lead-pipeline-search"
                            className="w-full pl-9 pr-8 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white" />
                        {search && (
                            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-100">
                                <X className="w-3.5 h-3.5 text-stone-400" />
                            </button>
                        )}
                    </div>
                    <button onClick={() => setFollowupOnly(v => !v)}
                        data-testid="lead-pipeline-followup-toggle"
                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors flex items-center gap-2 ${followupOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
                        <AlertTriangle className="w-4 h-4" /> Needs follow-up
                    </button>
                    {/* Board | List toggle (G8) */}
                    <div className="inline-flex rounded-lg border border-stone-200 bg-white overflow-hidden" data-testid="lead-view-toggle">
                        <button onClick={() => setView('board')}
                            className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 ${view === 'board' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                            <LayoutGrid className="w-4 h-4" /> Board
                        </button>
                        <button onClick={() => setView('list')}
                            className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-l border-stone-200 ${view === 'list' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                            <ListIcon className="w-4 h-4" /> List
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter bar (G9) — applies to both board + list */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="lead-filter-bar">
                <select value={fCoach} onChange={e => setFCoach(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                    <option value="">All coaches</option>
                    {coachOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={fStage} onChange={e => setFStage(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                    <option value="">All stages</option>
                    {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <select value={fBic} onChange={e => setFBic(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                    <option value="">All · ball-in-court</option>
                    <option value="action_needed">Action Needed</option>
                    <option value="waiting_on_lead">Waiting on Lead</option>
                </select>
                <select value={fBand} onChange={e => setFBand(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                    <option value="">All · staleness</option>
                    <option value="green">On track</option>
                    <option value="amber">Amber</option>
                    <option value="red">Red</option>
                </select>
                <select value={fSource} onChange={e => setFSource(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                    <option value="">All sources</option>
                    {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {anyFilterActive && (
                    <button onClick={clearFilters} className="px-2.5 py-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 flex items-center gap-1">
                        <X className="w-3.5 h-3.5" /> Clear
                    </button>
                )}
            </div>

            {/* Board */}
            {loading ? (
                <div className="py-24 text-center text-stone-400 text-sm">Loading lead pipeline…</div>
            ) : stages.length === 0 ? (
                <div className="py-24 text-center text-stone-400 text-sm">No pipeline stages configured.</div>
            ) : view === 'board' ? (
                <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4 min-w-max">
                        {stages.map(stage => {
                            const stageLeads = leadsByStage(stage.key);
                            const isOver = dragOverStage === stage.key;
                            const isWonColumn = stage.key === 'won';
                            return (
                                <div key={stage.key}
                                    className={`w-72 flex flex-col bg-stone-50 rounded-xl border ${isOver ? 'border-stone-400 bg-stone-100/60' : 'border-stone-200'} transition-colors`}
                                    onDragOver={e => handleDragOver(e, stage.key)}
                                    onDrop={e => handleDrop(e, stage.key)}
                                    data-testid={`lead-col-${stage.key}`}
                                >
                                    <div className="p-3 border-b border-stone-200 flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color || '#64748b' }} />
                                            <span className="text-xs font-semibold text-stone-700 truncate">{stage.label}</span>
                                        </div>
                                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-white text-stone-500 border border-stone-200 flex-shrink-0">
                                            {stageLeads.length}
                                        </span>
                                    </div>
                                    <div className="p-2 flex-1 space-y-2 min-h-[120px]">
                                        {stageLeads.length === 0 ? (
                                            <p className="text-xs text-stone-300 text-center py-8">No leads</p>
                                        ) : (
                                            stageLeads.map(lead => (
                                                <LeadCard key={lead.contact_id} lead={lead} isWonColumn={isWonColumn}
                                                    onDragStart={handleDragStart} onDragEnd={handleDragEnd} onOpen={openDrawer} />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                /* ── List view (G8) ── */
                <div className="overflow-x-auto bg-white rounded-xl border border-stone-200" data-testid="lead-list-view">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <SortHeader col="full_name">Lead</SortHeader>
                                <SortHeader col="company_name">Company</SortHeader>
                                <SortHeader col="pipeline_stage">Stage</SortHeader>
                                <SortHeader col="coach_assigned_name">Coach</SortHeader>
                                <SortHeader col="ball_in_court">Ball-in-court</SortHeader>
                                <SortHeader col="time_in_stage_days">Time in stage</SortHeader>
                                <SortHeader col="next_booking_at">Next booking</SortHeader>
                                <SortHeader col="lead_status">Status</SortHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedLeads.length === 0 ? (
                                <tr><td colSpan={8} className="px-3 py-12 text-center text-stone-400 text-sm">No leads match these filters.</td></tr>
                            ) : sortedLeads.map(lead => {
                                const band = lead.time_in_stage_band || 'green';
                                const bic = lead.ball_in_court;
                                return (
                                    <tr key={lead.contact_id} onClick={() => openDrawer(lead)}
                                        className="border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer"
                                        data-testid={`lead-row-${lead.contact_id}`}>
                                        <td className="px-3 py-2.5 font-medium text-stone-900 whitespace-nowrap">{lead.full_name}</td>
                                        <td className="px-3 py-2.5 text-stone-600">{lead.company_name || <span className="text-stone-300">—</span>}</td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: lead.pipeline_stage_color || stageColor(lead.pipeline_stage) }} />
                                                <span className="text-stone-700">{lead.pipeline_stage_label || stageLabel(lead.pipeline_stage)}</span>
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {lead.coach_assigned_name ? (
                                                <span className="inline-flex items-center gap-1.5"><CoachBadge name={lead.coach_assigned_name} /><span className="text-stone-700">{lead.coach_assigned_name}</span></span>
                                            ) : <span className="text-stone-300">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {bic && bic !== 'na'
                                                ? <Badge cls={BIC_CLS[bic]}>{lead.ball_in_court_label || bic}</Badge>
                                                : <span className="text-stone-300">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {lead.time_in_stage_days != null ? (
                                                <span className={`inline-flex items-center gap-1 ${BAND_TEXT[band]}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${BAND_DOT[band]}`} />{lead.time_in_stage_days}d
                                                </span>
                                            ) : <span className="text-stone-300">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">{fmtDate(lead.next_booking_at) || <span className="text-stone-300">—</span>}</td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">{lead.lead_status ? lead.lead_status.replace(/_/g, ' ') : <span className="text-stone-300">—</span>}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Working drawer (G7) */}
            {drawerLead && (
                <LeadDrawer
                    lead={drawerLead}
                    stages={stages}
                    teamMembers={teamMembers}
                    getAuthHeader={getAuthHeader}
                    onClose={closeDrawer}
                    onStageChange={() => loadBoard(debouncedSearch)}
                />
            )}

            <WonModal open={!!wonModal} onClose={() => !submitting && setWonModal(null)} lead={wonModal?.lead}
                products={products} onConfirm={confirmWon} busy={submitting} />
            <LostModal open={!!lostModal} onClose={() => !submitting && setLostModal(null)} lead={lostModal?.lead}
                reasonOptions={reasonOptions} onConfirm={confirmLost} busy={submitting} />
        </div>
    );
}
